"use client";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { onIdeaPrompt, type IdeaSource } from "@/lib/ideaPrompt";
import { track } from "@/lib/analytics";
import { useT } from "@/lib/i18n";

// Modale « Une idée, une remarque ? » — montée une seule fois au layout racine,
// ouverte par le bandeau du hero ou le lien du pied de page.
//
// Elle emprunte le tuyau des signalements (`/api/signalement`) avec un type à
// part : kind "idee". Côté studio, l'onglet Retours la range dans son propre
// filtre, distinct des problèmes signalés.

export function IdeaModal() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "failed">("idle");
  // Retenue à l'ouverture pour être rejouée à l'envoi : c'est le couple
  // ouverture/envoi d'une MÊME source qui dit si le formulaire décourage.
  const [source, setSource] = useState<IdeaSource>("footer");

  useEffect(
    () =>
      onIdeaPrompt((from) => {
        setMessage("");
        setContact("");
        setState("idle");
        setSource(from);
        setOpen(true);
        track.feedbackOpened({ source: from });
      }),
    [],
  );

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async () => {
    if (!message.trim() || state === "sending") return;
    setState("sending");
    try {
      const res = await fetch("/api/signalement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind: "idee", message, contact }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setState("done");
      track.feedbackSent({ source, has_contact: Boolean(contact.trim()) });
    } catch {
      setState("failed");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] grid place-items-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.div
            role="dialog"
            aria-label={t("Partager une idée ou une remarque")}
            className="relative w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-2xl"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            style={{ willChange: "transform, opacity" }}
          >
            <button
              onClick={() => setOpen(false)}
              aria-label={t("Fermer")}
              className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-fg"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>

            {state === "done" ? (
              <>
                {/* Icône et titre sur la MÊME ligne : empilés, la pastille flottait
                    seule au-dessus du texte et cassait l'alignement de l'en-tête. */}
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m5 13 4 4L19 7" />
                    </svg>
                  </span>
                  <h2 className="font-display text-lg font-bold">{t("Merci !")}</h2>
                </div>
                <p className="mt-3 text-sm text-fg-dim">
                  {t("On lit tout. C'est exactement ce qui nous aide à construire la suite.")}
                </p>
                <button
                  onClick={() => setOpen(false)}
                  className="mt-5 flex w-full items-center justify-center rounded-lg bg-accent py-2.5 text-sm font-semibold text-on-accent transition-colors hover:bg-accent-hover"
                >
                  {t("Fermer")}
                </button>
              </>
            ) : (
              <>
                {/* Ampoule et titre sur la MÊME ligne (demande de Jules) : la pastille
                    empilée au-dessus laissait un en-tête bancal. `pr-9` réserve la
                    place de la croix de fermeture, en absolu dans le coin. */}
                <div className="flex items-center gap-3 pr-9">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.4.3.6.8.6 1.2h6c0-.4.2-.9.6-1.2A6 6 0 0 0 12 3Z" />
                    </svg>
                  </span>
                  <h2 className="font-display text-lg font-bold">{t("Ton avis nous intéresse")}</h2>
                </div>
                <p className="mt-3 text-base text-fg-dim">
                  {t("Le site est en phase de lancement — tes retours décident de la suite.")}
                </p>

                {/* Pas d'autoFocus : sur téléphone il ferait surgir le clavier à l'ouverture. */}
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  rows={5}
                  placeholder={t("Ce que tu changerais, une marque ou un revendeur à ajouter, ton idée…")}
                  // fontSize 16px inline : sous 16px, iOS Safari zoome auto sur le champ au focus.
                  style={{ fontSize: "16px" }}
                  className="mt-4 w-full resize-none rounded-lg border border-border bg-bg px-3 py-2.5 text-fg placeholder:text-muted focus:border-accent focus:outline-none"
                />
                <p className="tnum mt-1 text-right text-[11px] text-muted">{message.length}/500</p>

                <label className="mt-2 block text-xs text-fg-dim">
                  {t("Ton email")}{" "}
                  <span className="text-muted">
                    (<strong className="font-semibold text-fg-dim">{t("facultatif")}</strong>{t(" — pour te répondre)")}
                  </span>
                  <input
                    type="email"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    maxLength={120}
                    placeholder={t("toi@exemple.fr")}
                    style={{ fontSize: "16px" }}
                    className="mt-1 w-full rounded-lg border border-border bg-bg px-3 py-2.5 text-fg placeholder:text-muted focus:border-accent focus:outline-none"
                  />
                </label>

                {state === "failed" && (
                  <p className="mt-3 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2 text-xs leading-snug text-warn">
                    {t("Échec de l'envoi — réessaie dans un instant.")}
                  </p>
                )}

                <button
                  onClick={() => void submit()}
                  disabled={!message.trim() || state === "sending"}
                  className="mt-4 flex w-full items-center justify-center rounded-lg bg-accent py-2.5 text-sm font-semibold text-on-accent transition-colors hover:bg-accent-hover disabled:opacity-40"
                >
                  {state === "sending" ? t("Envoi…") : t("Envoyer")}
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
